/**
 * ClientesPage — full-page route for /admin/clientes.
 * Stitch directory design: single white card, avatar initials, status pills, hover actions.
 */
import { useEffect, useMemo, useState } from "react";
import { Search, Plus, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { friendlyMessage } from "@/shared/lib/errors.js";
import type { Cliente } from "../types.js";
import { listClientes, createCliente, updateCliente } from "../services/clientes.service.js";
import { deriveSigla } from "../lib/sigla.js";
import { usePagination } from "../lib/usePagination.js";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_LABEL =
  "block font-mono text-[0.625rem] font-bold uppercase tracking-[0.15em] text-[#76746a]";

const FIELD_LABEL =
  "block font-mono text-[0.625rem] font-bold uppercase tracking-[0.15em] text-[#76746a] mb-2";

const UNDERLINE_INPUT =
  "w-full border-0 border-b border-[#ddd9cc] bg-transparent pb-2 text-sm text-[#1a1a18] outline-none placeholder:text-[#c5c2b8] focus:border-[#1a1a18] transition-colors";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Derive 2-char initials: first 2 chars of sigla, or first letters of the two first name words. */
function initials(c: Cliente): string {
  if (c.sigla.length >= 2) return c.sigla.slice(0, 2).toUpperCase();
  if (c.sigla.length === 1) return c.sigla.toUpperCase();
  const parts = c.nombre.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return (parts[0]?.slice(0, 2) ?? "?").toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export function ClientesPage() {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [cargando, setCargando] = useState(true);

  const [editId, setEditId] = useState<string | null>(null);
  const [nombre, setNombre] = useState("");
  const [sigla, setSigla] = useState("");
  const [siglaManual, setSiglaManual] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState("");

  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return clientes;
    return clientes.filter(
      (c) =>
        c.nombre.toLowerCase().includes(t) || c.sigla.toLowerCase().includes(t),
    );
  }, [clientes, q]);

  const { pageItems, page, totalPages, rangeStart, rangeEnd, prev, next } =
    usePagination(visibles);

  async function recargar() {
    setCargando(true);
    try {
      setClientes(await listClientes());
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    void recargar();
  }, []);

  function resetForm() {
    setEditId(null);
    setNombre("");
    setSigla("");
    setSiglaManual(false);
    setAbierto(false);
  }

  function onNombreChange(value: string) {
    setNombre(value);
    if (!siglaManual) setSigla(deriveSigla(value));
  }

  function onSiglaChange(value: string) {
    setSigla(value);
    setSiglaManual(value.trim().length > 0);
  }

  function startEdit(c: Cliente) {
    setEditId(c.id);
    setNombre(c.nombre);
    setSigla(c.sigla);
    setSiglaManual(c.sigla.trim().length > 0);
    setAbierto(true);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nombre.trim()) return;
    setGuardando(true);
    try {
      const siglaFinal = sigla.trim() || undefined;
      if (editId) {
        await updateCliente(editId, { nombre: nombre.trim(), sigla: siglaFinal });
        toast.success("Cliente actualizado");
      } else {
        await createCliente({ nombre: nombre.trim(), sigla: siglaFinal });
        toast.success("Cliente creado");
      }
      resetForm();
      await recargar();
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setGuardando(false);
    }
  }

  async function toggleActivo(c: Cliente) {
    try {
      await updateCliente(c.id, { activo: !c.activo });
      toast.success(c.activo ? "Cliente desactivado" : "Cliente activado");
      await recargar();
    } catch (err) {
      toast.error(friendlyMessage(err));
    }
  }

  const enEdicion = editId !== null;

  return (
    <main className="min-h-full bg-[#f4f3ee] px-4 py-4 sm:px-8 sm:py-8 flex justify-center items-start">
      <div className="w-full max-w-[1760px] bg-white rounded-[24px] border border-[#ddd9cc] shadow-sm">
        {/* ── Card header ────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between p-4 pb-4 sm:p-8 sm:pb-6">
          <div>
            <h1 className="text-[#1a1a18] text-[1.75rem] font-bold leading-tight mb-2">
              Gestión de Clientes
            </h1>
            <p className="text-[#76746a] text-sm leading-relaxed max-w-[480px]">
              Directorio corporativo de entidades asociadas. Gestione accesos y perfiles
              institucionales.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (abierto) resetForm();
              else {
                resetForm();
                setAbierto(true);
              }
            }}
            className="flex items-center justify-center gap-2 bg-[#1a1a18] text-white rounded-[20px] px-6 py-3 font-bold text-sm w-full sm:w-auto mt-4 sm:mt-0 sm:ml-6 hover:bg-[#2a2a26] transition-colors"
          >
            <Plus className="size-4" aria-hidden />
            Agregar cliente
          </button>
        </div>
        <div className="border-b border-[#ddd9cc]" />

        {/* ── Card body ──────────────────────────────────────────────────── */}
        <div className="p-4 sm:p-8">
          {/* Inline create / edit form */}
          {abierto && (
            <form onSubmit={onSubmit} className="bg-[#f4f3ee] rounded-xl p-6 mb-6">
              <span className={SECTION_LABEL}>
                {enEdicion ? "Editar cliente" : "Nuevo cliente"}
              </span>
              <div className="grid gap-6 sm:grid-cols-2 mt-4">
                <div>
                  <label htmlFor="cli-nombre" className={FIELD_LABEL}>
                    Nombre del cliente
                  </label>
                  <input
                    id="cli-nombre"
                    type="text"
                    value={nombre}
                    onChange={(e) => onNombreChange(e.target.value)}
                    placeholder="Ferreyros Caterpillar Perú"
                    className={UNDERLINE_INPUT}
                  />
                </div>
                <div>
                  <label htmlFor="cli-sigla" className={FIELD_LABEL}>
                    Sigla (opcional)
                  </label>
                  <input
                    id="cli-sigla"
                    type="text"
                    value={sigla}
                    onChange={(e) => onSiglaChange(e.target.value)}
                    placeholder="se deriva del nombre"
                    className={UNDERLINE_INPUT}
                  />
                </div>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="submit"
                  disabled={guardando || !nombre.trim()}
                  className="bg-[#1a1a18] text-white rounded-[20px] px-5 py-2.5 text-sm font-bold hover:bg-[#2a2a26] transition-colors disabled:opacity-60 disabled:pointer-events-none"
                >
                  {guardando
                    ? "Guardando…"
                    : enEdicion
                      ? "Guardar cambios"
                      : "Agregar cliente"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="border border-[#ddd9cc] rounded-[20px] px-5 py-2.5 text-sm font-bold text-[#1a1a18] hover:bg-white transition-colors"
                >
                  {enEdicion ? "Cancelar" : "Cerrar"}
                </button>
              </div>
            </form>
          )}

          {/* Search bar */}
          <div className="bg-[#f4f3ee] border-b border-[#ddd9cc] rounded-t-lg flex items-center px-4 py-3 mb-6">
            <Search className="size-4 text-[#76746a] mr-3 shrink-0" aria-hidden />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o sigla…"
              className="flex-1 bg-transparent text-sm text-[#1a1a18] outline-none placeholder:text-[#c5c2b8]"
            />
          </div>

          {/* States: loading / empty / no-results / list */}
          {cargando ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-[#76746a]">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Cargando clientes…
            </div>
          ) : clientes.length === 0 ? (
            <p className="py-12 text-center text-sm text-[#76746a]">
              No hay clientes todavía.
            </p>
          ) : visibles.length === 0 ? (
            <p className="py-12 text-center text-sm text-[#76746a]">
              Sin resultados para «{q}».
            </p>
          ) : (
            <div className="flex flex-col">
              {pageItems.map((c) => (
                <div
                  key={c.id}
                  className={[
                    "group flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 border-b border-[#ddd9cc]",
                    "hover:bg-[#f4f3ee] transition-colors px-4 -mx-4 rounded-lg last:border-0",
                    !c.activo && "opacity-60",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {/* Left: avatar + name + sigla */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#e5e2e1] border border-[#ddd9cc] flex items-center justify-center font-bold text-[#1a1a18] text-sm shrink-0">
                      {initials(c)}
                    </div>
                    <div>
                      <p className="font-bold text-[#1a1a18] text-sm">{c.nombre}</p>
                      <p className="font-mono text-[0.6rem] font-bold uppercase tracking-[0.12em] text-[#76746a] mt-0.5">
                        SIGLA: {c.sigla}
                      </p>
                    </div>
                  </div>

                  {/* Right: status pill + hover action buttons */}
                  <div className="flex items-center gap-3 flex-wrap mt-2 sm:mt-0">
                    {c.activo ? (
                      <span className="px-3 py-1 rounded-full bg-[#e7e6df] font-mono text-[0.6rem] font-bold uppercase tracking-[0.12em] text-[#1a1a18] flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#fed01b]" aria-hidden />
                        Activo
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full bg-[#e5e2e1] font-mono text-[0.6rem] font-bold uppercase tracking-[0.12em] text-[#76746a] flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#999]" aria-hidden />
                        Inactivo
                      </span>
                    )}

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(c)}
                        className="text-xs border border-[#ddd9cc] rounded-[12px] px-3 py-1 font-bold text-[#1a1a18] hover:bg-white transition-colors"
                      >
                        Editar
                      </button>
                      {c.activo ? (
                        <button
                          type="button"
                          onClick={() => void toggleActivo(c)}
                          className="text-xs border border-[#ddd9cc] rounded-[12px] px-3 py-1 font-bold text-[#76746a] hover:bg-white transition-colors"
                        >
                          Desactivar
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void toggleActivo(c)}
                          className="text-xs border border-[#ddd9cc] rounded-[12px] px-3 py-1 font-bold text-[#1a1a18] hover:bg-white transition-colors"
                        >
                          Activar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer: conteo + paginación */}
          {!cargando && visibles.length > 0 && (
            <div className="flex items-center justify-between border-t border-[#ddd9cc] pt-4 mt-4">
              <span className="font-mono text-[0.625rem] font-bold uppercase tracking-[0.12em] text-[#76746a]">
                {`Mostrando ${rangeStart}–${rangeEnd} de ${clientes.length} clientes`}
              </span>
              <div className="flex items-center gap-2">
                {totalPages > 1 && (
                  <span className="font-mono text-[0.625rem] font-bold uppercase tracking-[0.12em] text-[#76746a]">
                    {`${page} / ${totalPages}`}
                  </span>
                )}
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={prev}
                  aria-label="Página anterior"
                  className="p-1 rounded hover:bg-[#f4f3ee] transition-colors disabled:opacity-30"
                >
                  <ChevronLeft className="size-5 text-[#76746a]" aria-hidden />
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={next}
                  aria-label="Página siguiente"
                  className="p-1 rounded hover:bg-[#f4f3ee] transition-colors disabled:opacity-30"
                >
                  <ChevronRight className="size-5 text-[#76746a]" aria-hidden />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
